import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, firstValueFrom, take } from 'rxjs';
import { IntelligenceModels } from '@models/intelligence.model';
import { PersonModel } from '@models/person.model';
import { PersonGroupsModel } from '@models/person-groups.model';
import { PersonActions } from '@states/person/person.actions';
import { PersonGroupsActions } from '@states/person-groups/person-groups.actions';
import { personGroupsFeature } from '@states/person-groups/person-groups.feature';
import { UiButtonComponent, UiDatasetTableComponent, UiPersonTileComponent, UiSelectedItemsSnackbarComponent } from '@ui';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { PersonDialogService } from '../../../../../services/person-dialog.service';
import { PersonGroupsDialogService } from '../../../../../services/person-groups-dialog.service';
import { createPersonGroupsTableConfig } from '../../../../../pages/database/pages/person/pages/person-groups/person-groups-table.util';
import { api } from '@consts/url.const';
import { routerSegments } from '@consts/routes';

@Component({
  selector: 'app-person-groups-result',
  standalone: true,
  imports: [CommonModule, FormsModule, UiPersonTileComponent, UiButtonComponent, UiDatasetTableComponent, UiSelectedItemsSnackbarComponent, TranslatePipe, MatTooltipModule],
  templateUrl: './person-groups-result.component.html',
  styleUrls: ['./person-groups-result.component.scss'],
})
export class PersonGroupsResultComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly personDialogService = inject(PersonDialogService);
  private readonly personGroupsDialogService = inject(PersonGroupsDialogService);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  readonly resultsService = inject(IntelligenceResultsService);

  private readonly processingIds = this.store.selectSignal(
    personGroupsFeature.selectPersonGroupProcessingIds,
  );

  private readonly removingPersonIds = this.store.selectSignal(
    personGroupsFeature.selectPersonGroupMemberRemovingIds,
  );

  /** Read directly from the service — self-updates without any parent binding. */
  readonly response = computed<
    | IntelligenceModels.PersonGroups.Response
    | IntelligenceModels.PersonGroups.MembersResponse
    | null
  >(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'person-groups') return null;
    return result.data as
      | IntelligenceModels.PersonGroups.Response
      | IntelligenceModels.PersonGroups.MembersResponse;
  });

  readonly groups = computed<PersonGroupsModel.PersonGroupListDocument[]>(() => {
    const data = this.response();
    return data && 'groups' in data ? data.groups : [];
  });

  readonly members = computed<IntelligenceModels.PersonGroups.MembersResponse | null>(() => {
    const data = this.response();
    return data && 'persons' in data ? data : null;
  });

  readonly loadingGroupId = signal<string | null>(null);

  protected readonly selectedPersonIds = signal<Set<number>>(new Set<number>());
  protected readonly selectedPersonsCount = computed(() => this.selectedPersonIds().size);

  /** Memoized so the reset effect below only fires when the open group actually changes. */
  private readonly membersGroupId = computed<string | null>(() => this.members()?.groupId ?? null);

  protected readonly groupsTableConfig = computed(() =>
    createPersonGroupsTableConfig({
      onRowClick: (group) => this.openGroup(group),
      processingIds: () => this.processingIds(),
    }),
  );

  constructor() {
    // Publish this view's actions into the shared Cmd+K context bar; clear them
    // when this component unmounts (result type switches away from person-groups).
    effect(
      () => {
        const membersData = this.members();
        this.resultsService.setHeaderActions(
          membersData
            ? [
                {
                  icon: 'ui-icon-plus',
                  tooltip: 'PERSON_GROUPS.DETAIL.ADD_PERSONS',
                  onClick: () => this.addPersonsToGroup(),
                },
                {
                  icon: 'ui-icon-new-tab',
                  tooltip: 'PERSON_GROUPS_RESULT.OPEN_GROUP_PAGE',
                  onClick: () =>
                    this.openInNewTab([routerSegments.personGroups, membersData.groupId]),
                },
              ]
            : [],
        );
      },
      { allowSignalWrites: true },
    );
    // The component survives drill-in/drill-out, so a stale selection must not
    // leak into another group's members view.
    effect(
      () => {
        this.membersGroupId();
        this.selectedPersonIds.set(new Set<number>());
      },
      { allowSignalWrites: true },
    );
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  async openGroup(group: PersonGroupsModel.PersonGroupListDocument): Promise<void> {
    if (this.loadingGroupId()) return;
    this.loadingGroupId.set(group._id);
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IntelligenceModels.PersonGroups.MembersResponse>(
          `${api.intelligence.personGroups}/${group._id}/persons`,
        ),
      );
      const label = `${response.count} of ${response.totalCount} · ${response.groupName}`;
      // Use overlay so this drill-down does not insert a new history entry
      // and break the sequential message↔history mapping.
      this.resultsService.setOverlay(response, 'person-groups', label);
    } finally {
      this.loadingGroupId.set(null);
    }
  }

  /** Persist recognition through the production path; snapshot patched on success. */
  recognize(person: IntelligenceModels.Persons.Person): void {
    if (person.trainingStatus === 1 || !person.personId) return;
    const personId = person.personId;
    this.personDialogService
      .openConfirmRecognition({ personId })
      .afterClosed()
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((recognitionResult) =>
        this.store.dispatch(
          PersonActions.trainSavedPerson({
            request: {
              personId,
              positiveTrackerIds: recognitionResult.positiveTrackerIds,
              negativeTrackerIds: recognitionResult.negativeTrackerIds,
            },
          }),
        ),
      );
  }

  /** Rename through the production edit dialog; snapshot patched on success. */
  edit(person: IntelligenceModels.Persons.Person): void {
    if (!person.personId) return;
    this.personDialogService.openEditPerson({
      personId: person.personId,
      name: person.name,
      bestImage: person.bestImage ?? '',
      trainingStatus: person.trainingStatus ?? PersonModel.TrainingStatus.Untrained,
    });
  }

  /** Detach the person from the open group; snapshot patched on success. */
  remove(person: IntelligenceModels.Persons.Person): void {
    const personId = person.personId;
    if (personId == null || this.isPersonRemoving(personId)) return;
    this.dispatchRemoveMembersRequest([personId], () =>
      this.updatePersonSelection(personId, false),
    );
  }

  /** Open the production add-persons dialog for the currently open group. */
  protected addPersonsToGroup(): void {
    const members = this.members();
    if (!members) return;
    const memberIds = members.persons
      .map((p) => p.personId)
      .filter((id): id is number => id != null);
    this.personGroupsDialogService.openAddPersonsToGroup(
      members.groupId,
      members.groupName,
      memberIds,
    );
  }

  protected isPersonSelected(personId: number | undefined): boolean {
    return personId != null && this.selectedPersonIds().has(personId);
  }

  protected updatePersonSelection(personId: number | undefined, selected: boolean): void {
    if (personId == null) return;
    this.selectedPersonIds.update((selectedPersonIds) => {
      const nextSelectedPersonIds = new Set(selectedPersonIds);
      if (selected) {
        nextSelectedPersonIds.add(personId);
      } else {
        nextSelectedPersonIds.delete(personId);
      }
      return nextSelectedPersonIds;
    });
  }

  protected selectAllDisplayedPersons(): void {
    const members = this.members();
    if (!members) return;
    const displayedPersonIds = members.persons
      .map((person) => person.personId)
      .filter((personId): personId is number => personId != null && !this.isPersonRemoving(personId));
    this.selectedPersonIds.update(
      (selectedPersonIds) => new Set([...selectedPersonIds, ...displayedPersonIds]),
    );
  }

  protected clearSelectedPersons(): void {
    this.selectedPersonIds.set(new Set<number>());
  }

  protected removeSelectedMembers(): void {
    const personIds = Array.from(this.selectedPersonIds());
    if (personIds.some((personId) => this.isPersonRemoving(personId))) return;
    this.dispatchRemoveMembersRequest(personIds, () => this.clearSelectedPersons());
  }

  protected isPersonRemoving(personId: number | undefined): boolean {
    return personId != null && this.removingPersonIds().includes(personId);
  }

  protected hasRemovingSelectedPersons(): boolean {
    return Array.from(this.selectedPersonIds()).some((personId) =>
      this.removingPersonIds().includes(personId),
    );
  }

  private dispatchRemoveMembersRequest(personIds: number[], onSuccess: () => void): void {
    const members = this.members();
    if (!members || personIds.length === 0) return;
    const requestId = `${members.groupId}_${Date.now()}_${Math.random()}`;
    this.actions$
      .pipe(
        ofType(PersonGroupsActions.removePersonsFromGroupSuccess),
        filter((action) => action.requestId === requestId),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => onSuccess());
    this.store.dispatch(
      PersonGroupsActions.requestRemovePersonsFromGroup({
        groupId: members.groupId,
        groupName: members.groupName,
        personIds,
        requestId,
      }),
    );
  }

  openGroupsPage(): void {
    this.openInNewTab([routerSegments.personGroups]);
  }

  protected createGroup(): void {
    this.personGroupsDialogService.openCreate();
  }

  protected onGroupRowAction(event: PersonGroupsModel.DatasetRowActionEvent): void {
    switch (event.actionId) {
      case PersonGroupsModel.RowActionId.Edit:
        this.personGroupsDialogService.openEdit(event.row._id, event.row.name);
        break;
      case PersonGroupsModel.RowActionId.Delete:
        this.store.dispatch(
          PersonGroupsActions.requestDeletePersonGroup({ id: event.row._id, name: event.row.name }),
        );
        break;
    }
  }

  /** Open the target app page in a new tab so the current Cmd+K session stays open. */
  private openInNewTab(segments: string[]): void {
    const tree = this.router.createUrlTree(['/', routerSegments.database, ...segments]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
